import { ParentSize } from '@visx/responsive'
import { useEffect, useMemo, useRef, useState } from 'react'
import Spinner from 'app/components/Spinner'
import DateGroupSelector from 'app/components/viz/DateGroupSelector'
import GraphHeader from 'app/components/viz/GraphHeader'
import GroupedBarStackGraph from 'app/components/viz/graphs/GroupedBarStackGraph'
import SegmentControl from 'app/components/viz/SegmentControl'
import { COLLECTIONS_LABEL, collectionsSeries, productionLabelFor, productionSeries } from 'lib/revenueChartData'
import type { BarsGroupedBy, ProductionType } from 'lib/revenueChartData'
import type { RevenueDailyMetric } from 'types/analytics'

// Left to the band's padding the gap is about 7px at any bucket count, which
// is not enough to keep a pair's two currency labels apart.
const PAIR_GAP = 32

export default function RevenueBarChart({
  dailyMetrics,
  isLoading,
}: {
  dailyMetrics: RevenueDailyMetric[]
  isLoading: boolean
}) {
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const [productionType, setProductionType] = useState<ProductionType>(`net`)
  const [barsGroupedBy, setBarsGroupedBy] = useState<BarsGroupedBy>(`months`)

  useEffect(() => {
    if (!dailyMetrics.length) return
    if (barsGroupedBy !== `days` && dailyMetrics.length <= 7) {
      setBarsGroupedBy(`days`)
    } else if (barsGroupedBy === `months` && dailyMetrics.length <= 31) {
      setBarsGroupedBy(`days`)
    } else if ((barsGroupedBy === `days` || barsGroupedBy === `weeks`) && dailyMetrics.length > 31) {
      setBarsGroupedBy(`months`)
    }
  }, [dailyMetrics, barsGroupedBy])

  // The grouping selector and the data length can disagree for a render after
  // the date range changes. Both series stay empty until they agree, so the
  // chart never draws a month bar against a week's worth of data.
  const showSeries = useMemo(() => {
    if (!dailyMetrics.length) return false
    if (barsGroupedBy !== `days` && dailyMetrics.length <= 7) return false
    if ((barsGroupedBy === `days` || barsGroupedBy === `weeks`) && dailyMetrics.length > 31) return false
    return true
  }, [dailyMetrics, barsGroupedBy])

  const data = useMemo(
    () => (showSeries ? productionSeries(dailyMetrics, barsGroupedBy, productionType) : []),
    [showSeries, dailyMetrics, barsGroupedBy, productionType],
  )

  const collectionsData = useMemo(
    () => (showSeries ? collectionsSeries(dailyMetrics, barsGroupedBy) : []),
    [showSeries, dailyMetrics, barsGroupedBy],
  )

  const tickFormat = barsGroupedBy === `months` ? `MMM YYYY` : `MMM D`

  const productionLabel = productionLabelFor(productionType)

  return (
    <div className="min-w-[800px] rounded-lg border border-default p-6">
      <GraphHeader
        leftSection={[{ title: `Production and Collections`, count: null }]}
        rightSection={[
          <SegmentControl
            key={1}
            label="Production Type"
            buttons={[
              {
                label: `Gross`,
                onClick: () => setProductionType(`gross`),
                isActive: productionType === `gross`,
              },
              {
                label: `Net`,
                onClick: () => setProductionType(`net`),
                isActive: productionType === `net`,
              },
            ]}
          />,
          <DateGroupSelector
            key={2}
            defaultGroup={barsGroupedBy}
            onGroupChange={(group) => setBarsGroupedBy(group)}
            disableDays={dailyMetrics.length > 31}
            disableWeeks={dailyMetrics.length > 31 || dailyMetrics.length <= 7}
            disableMonths={dailyMetrics.length <= 31}
          />,
        ]}
      />
      <div className="overflow-x-auto">
        <div ref={graphContainerRef} className="relative h-[500px] min-w-[1080px]">
          <ParentSize>
            {() => (
              <>
                {isLoading && (
                  <div
                    style={{
                      width: graphContainerRef.current?.clientWidth!,
                      height: graphContainerRef.current?.clientHeight! + 50,
                    }}
                    className="absolute left-0 top-0 z-10 flex items-center justify-center bg-surface bg-opacity-70"
                  >
                    <Spinner size={10} />
                  </div>
                )}
                <GroupedBarStackGraph
                  allKeys={[productionLabel, COLLECTIONS_LABEL]}
                  colorMap={{ [productionLabel]: `blue`, [COLLECTIONS_LABEL]: `green` }}
                  valuePrefix="$"
                  width={graphContainerRef.current?.clientWidth!}
                  height={graphContainerRef.current?.clientHeight!}
                  data={data}
                  comparedData={collectionsData}
                  dimComparedSeries={false}
                  pairGap={PAIR_GAP}
                  tickFormat={tickFormat}
                  now={null}
                  scheduledData={undefined}
                />
              </>
            )}
          </ParentSize>
        </div>
      </div>
    </div>
  )
}
